import {
  AwardIcon,
  DollarSignIcon,
  PhoneIcon,
  SendIcon,
  UserCheckIcon,
  type LucideIcon,
} from "lucide-react";

import halloweenInterviews from "@/assets/leaderboard/halloween-interviews.jpg";
import halloweenPlacements from "@/assets/leaderboard/halloween-placements.jpg";
import halloweenScreens from "@/assets/leaderboard/halloween-screens.jpg";
import halloweenSubmittals from "@/assets/leaderboard/halloween-submittals.jpg";
import halloweenBackdrop from "@/assets/racetrack/halloween-backdrop-playful.png";
import { Column, Row, Text } from "@/components";
import { HexagonIconBadge } from "@/components/leaderboard/HexagonIconBadge";
import { MetricLeadersHoverCard } from "@/components/leaderboard/MetricLeadersHoverCard";
import { MostRevenueAndRacetrackContainer } from "@/components/leaderboard/MostRevenueAndRacetrackContainer";
import { TopPerformerLeaders } from "@/components/leaderboard/TopPerformerLeaders";
import { Card, CardContent } from "@/components/ui/card";
import {
  INTERVIEWS_GRADIENT,
  IS_LEADERBOARD_HALLOWEEN,
  PLACEMENTS_GRADIENT,
  REVENUE_GRADIENT,
  SCREENS_GRADIENT,
  SUBMITTALS_GRADIENT,
} from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { leaderboardMetrics } from "@/lib/leaderboardMetrics";
import { cn } from "@/lib/utils";

import type {
  HexGradient,
  LeaderboardQueryParams,
  LeaderboardUser,
} from "@/types";

// Each dark card has its own Halloween scene while retaining its metric color.
const CARD_THEMES = {
  revenue: {
    light: "bg-gradient-to-br from-[#4ade803d] via-[#4ade802e] to-[#4ade801f]",
    halloween: "from-[#170e26]/60 via-[#170e26]/30 to-transparent",
    background: halloweenBackdrop,
    badgeBackdrop: "#d5f7df",
  },
  placements: {
    light: "bg-gradient-to-br from-[#fbbf243d] via-[#fbbf242e] to-[#fbbf241f]",
    halloween: "from-[#241126]/45 via-[#241126]/20 to-transparent",
    background: halloweenPlacements,
    badgeBackdrop: "#feedc8",
  },
  screens: {
    light: "bg-gradient-to-br from-[#38bdf83d] via-[#38bdf82e] to-[#38bdf81f]",
    halloween: "from-[#101b3c]/45 via-[#101b3c]/20 to-transparent",
    background: halloweenScreens,
    badgeBackdrop: "#d0f0fe",
  },
  submittals: {
    light: "bg-gradient-to-br from-[#a78bfa3d] via-[#a78bfa2e] to-[#a78bfa1f]",
    halloween: "from-[#261235]/45 via-[#261235]/20 to-transparent",
    background: halloweenSubmittals,
    badgeBackdrop: "#ede7fe",
  },
  interviews: {
    light: "bg-gradient-to-br from-[#34d3993d] via-[#34d3992e] to-[#34d3991f]",
    halloween: "from-[#10292d]/45 via-[#10292d]/20 to-transparent",
    background: halloweenInterviews,
    badgeBackdrop: "#d1fae5",
  },
};

type CardTheme = (typeof CARD_THEMES)[keyof typeof CARD_THEMES];

export const LeaderboardTopPerformerCards = ({
  users,
  queryParams,
  showRacetrack,
}: {
  users: LeaderboardUser[];
  queryParams: LeaderboardQueryParams;
  showRacetrack: boolean;
}) => {
  /* Render */
  const render = () => (
    <Column className="gap-6">
      {showRacetrack && (
        <MostRevenueAndRacetrackContainer queryParams={queryParams} />
      )}
      <Row className="items-stretch gap-6">
        {!showRacetrack && (
          <TopPerformerCard
            users={users}
            metricKey="revenue"
            label="Most Revenue"
            icon={DollarSignIcon}
            hexGradient={REVENUE_GRADIENT}
            theme={CARD_THEMES.revenue}
            isHalloween={IS_LEADERBOARD_HALLOWEEN}
            formatValue={formatting.compactUsd}
          />
        )}
        <TopPerformerCard
          users={users}
          metricKey="placements"
          label="Most Placements"
          icon={AwardIcon}
          hexGradient={PLACEMENTS_GRADIENT}
          theme={CARD_THEMES.placements}
          isHalloween={IS_LEADERBOARD_HALLOWEEN}
        />
        <TopPerformerCard
          users={users}
          metricKey="screens"
          label="Most Screens"
          icon={PhoneIcon}
          hexGradient={SCREENS_GRADIENT}
          theme={CARD_THEMES.screens}
          isHalloween={IS_LEADERBOARD_HALLOWEEN}
        />
        <TopPerformerCard
          users={users}
          metricKey="submittals"
          label="Most Submits"
          icon={SendIcon}
          hexGradient={SUBMITTALS_GRADIENT}
          theme={CARD_THEMES.submittals}
          isHalloween={IS_LEADERBOARD_HALLOWEEN}
        />
        <TopPerformerCard
          users={users}
          metricKey="interviews"
          label="Most Interviews"
          icon={UserCheckIcon}
          hexGradient={INTERVIEWS_GRADIENT}
          theme={CARD_THEMES.interviews}
          isHalloween={IS_LEADERBOARD_HALLOWEEN}
        />
      </Row>
    </Column>
  );

  return render();
};

const TopPerformerCard = ({
  users,
  metricKey,
  label,
  icon,
  hexGradient,
  theme,
  isHalloween,
  formatValue,
}: {
  users: LeaderboardUser[];
  metricKey: keyof LeaderboardUser;
  label: string;
  icon: LucideIcon;
  hexGradient: HexGradient;
  theme: CardTheme;
  isHalloween: boolean;
  formatValue?: (value: number) => string;
}) => {
  const maxValue = Math.max(...users.map((u) => u[metricKey] as number), 0);
  const metricLeaders = users
    .filter((u) => (u[metricKey] as number) === maxValue && maxValue > 0)
    .sort((a, b) => a.firstName.localeCompare(b.firstName));

  /* Render */
  const render = () => (
    <Card
      className={cn(
        "relative min-w-0 flex-1 overflow-hidden rounded-2xl",
        isHalloween
          ? "border-amber-200/30 bg-[#1e1431] shadow-sm"
          : cn("border-slate-200 shadow-sm", theme.light),
      )}
    >
      {isHalloween && (
        <>
          <img
            src={theme.background}
            alt=""
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
          <Column
            className={cn(
              "pointer-events-none absolute inset-0 bg-gradient-to-br",
              theme.halloween,
            )}
          />
        </>
      )}
      <CardContent className="relative px-5 pb-8 pt-4">
        <Column className="gap-3">
          <Column className="gap-0">
            <Row className="items-center justify-between gap-3">
              <Text
                className={cn(
                  "text-xs font-semibold uppercase leading-tight tracking-wider",
                  isHalloween ? "text-slate-200" : "text-slate-800",
                )}
              >
                {label}
              </Text>
              <MetricLeadersHoverCard
                title={label}
                icon={icon}
                gradient={hexGradient}
                leaders={leaderboardMetrics.topN(users, metricKey, 3)}
                valueOf={(u) => u[metricKey] as number}
                format={
                  formatValue ??
                  ((value) => Math.ceil(value).toLocaleString("en-US"))
                }
                isHalloween={isHalloween}
                badgeBackdropColor={
                  isHalloween ? theme.badgeBackdrop : undefined
                }
              >
                <Row className="cursor-default">
                  <HexagonIconBadge
                    icon={icon}
                    gradient={hexGradient}
                    backdropColor={
                      isHalloween ? theme.badgeBackdrop : undefined
                    }
                  />
                </Row>
              </MetricLeadersHoverCard>
            </Row>
            <Text
              className={cn(
                "-mt-1 text-5xl font-bold tabular-nums leading-none tracking-tight",
                isHalloween ? "text-white" : "text-slate-900",
              )}
            >
              {maxValue > 0
                ? formatValue
                  ? formatValue(maxValue)
                  : maxValue
                : "—"}
            </Text>
          </Column>
          <TopPerformerLeaders
            leaders={metricLeaders}
            isHalloween={isHalloween}
          />
        </Column>
      </CardContent>
    </Card>
  );

  return render();
};
